import React, { useEffect } from 'react';
import { SkeletonBlock, SkeletonText } from '@zapier/design-system';
import styled from '@emotion/styled';
import { UpNextArticle } from 'data/SingleArticle/types';
import { useUpNextArticles } from 'hooks/parsely/useUpNextArticle';
import { mediaQueries } from 'components/mediaQueries';
import { Spacer } from 'components/Spacer/Spacer';
import { UpNextCard } from './UpNextCard';

type Props = {
  article: UpNextArticle | null;
  slugToExclude: string;
  /**
   * Useful when you want to send the UpNext Article slug
   * to the consumer of this component.
   * Generally used for Parse.ly recommendations.
   */
  onUpNextArticleSlugReady?: (slug: string) => void;
};

function Loading() {
  return (
    <>
      <CardWrapper>
        <SkeletonBlock width="100%" height={218} />
        <TextWrapper>
          <SkeletonText lines={1} size="heading" width="100%" />
          <SkeletonText lines={6} size="body" width="100%" />
        </TextWrapper>
      </CardWrapper>
      <Spacer hasLine={true} />
    </>
  );
}

export const UpNext = ({
  article,
  onUpNextArticleSlugReady = () => {},
  slugToExclude,
}: Props) => {
  const {
    articleSlug,
    error,
    fetchUpNextArticle,
    loading,
    upNextArticle,
  } = useUpNextArticles({ slugsToDedupe: slugToExclude });

  useEffect(() => {
    /**
     * We would only want to fetch the `UpNext` article
     * if we don't have a hardcoded article from Contentful
     */
    if (!article?.slug) {
      fetchUpNextArticle();
    }
  }, [article?.slug]);

  useEffect(() => {
    const upNextArticleSlug = article?.slug ?? articleSlug;

    onUpNextArticleSlugReady(upNextArticleSlug);
  }, [article?.slug, articleSlug]);

  if (article?.slug) {
    return <UpNextCard article={article} />;
  }

  if (loading) {
    return <Loading />;
  }

  if (error || !upNextArticle) {
    return null;
  }

  return <UpNextCard article={upNextArticle!} />;
};

const TextWrapper = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

const CardWrapper = styled.div`
  display: grid;
  grid-column-gap: 20px;
  grid-template-columns: 1fr 1fr;
  padding: 0 20px;
  ${mediaQueries.tablet} {
    grid-row-gap: 25px;
    padding: 0;
  }
`;
